/* ============================================================================
   Stickr Lab — Durable design assets  (assets/sl-design-assets.js)   [D-056]

   Turns a canonical Builder payload into the THREE durable production assets and
   packages them for Shopify's cart:

     1. the ORIGINAL customer file, unmodified
     2. a rendered PREVIEW (reference of intent, not print-ready)
     3. the canonical CONFIG json

   HOW THEY ARE STORED — and why there is no upload service here.

   Shopify natively accepts a File inside a line-item property. It uploads the file
   to its own CDN and stores the resulting URL as the property value, which then
   flows onto the order unchanged. No app, no Admin API token, no object store, no
   serverless function. That native mechanism is the entire backend.

   THE CATCH THAT DICTATES THIS FILE'S EXISTENCE: `/cart/add.js` was being called
   with `Content-Type: application/json` and `JSON.stringify`, and JSON cannot carry
   a file. Shopify hit exactly this in its own Dawn theme (Shopify/dawn#731, fixed by
   PR #745) and solved it by sending `FormData` instead. So do we. Note the request
   must use the FLAT form shape (`id`, `quantity`, `properties[...]`) rather than the
   `{items:[…]}` JSON shape — the latter has no way to express a file.

   DURABILITY IS OBSERVED, NOT GUARANTEED. Shopify documents neither a retention
   policy nor a TTL for these uploads, and a Shopify moderator confirmed there is no
   documented merchant-facing deletion flow. Files on completed orders are widely
   reported to persist indefinitely. Two cheap hedges are therefore built in here:
   the design id, schema version, builder version and a human summary also travel as
   PLAIN TEXT properties (see sl-cart-adapter.js), and every uploaded filename embeds
   the designId and creation date so a future cleanup pass can identify orphans by
   name alone (D-056, post-launch debt — not built).

   The pure half of this file (filenames, MIME, config json) is Node-testable; only
   the Blob/FormData half needs a browser.
   ============================================================================ */
(function (root) {
  'use strict';

  var PROP_ORIGINAL   = '_artwork_original';
  var PROP_PREVIEW    = '_artwork_preview';
  var PROP_CONFIG     = '_design_config';
  /* The production file (sl-production-artwork.js): physical size, embedded
     raster, editable vectors, named cut path. This is what the operator opens
     in Illustrator to make the order — the other three exist to prove, record
     and recover it. It is attached ADDITIVELY for now: nothing depends on it
     yet, so a failure to produce one cannot cost a customer their sale. */
  var PROP_PRODUCTION = '_production_artwork';
  /* The PRINT artwork, only when the customer's file had to be converted to
     be printed (a PDF page, a TIFF, an SVG ...): the exact pixels the proof
     was made from, which production places. The original still travels
     unchanged in PROP_ORIGINAL. Customer sheets only; absent everywhere else. */
  var PROP_PRINT      = '_artwork_print';
  /* A builder sticker sheet, laid out: every sticker in place, its kiss cuts, and the
     finished sheet's outer cut (sl-sheet-production.js). Builder sheets only. It travels
     BESIDE the single-sticker production file, never instead of it. */
  var PROP_SHEET_LAYOUT = '_sheet_layout';
  /* THE CUSTOMER'S FILE, BYTE FOR BYTE (D-172). Shopify re-encodes every
     raster image it stores - measured 10 Oct 2026: a CMYK FOGRA39 JPEG came
     back as an sRGB JPEG, an Adobe RGB PNG as sRGB, a TIFF as a JPEG - so the
     file behind PROP_ORIGINAL is no longer the customer's once it is stored.
     A real ZIP is stored exactly (measured byte-identical), so the same bytes
     ALSO travel inside a one-entry, uncompressed ZIP, and HQ prefers it.
     PROP_ORIGINAL is unchanged and still drives everything that already reads
     it (cart thumbnails, saved designs, older HQ). PDFs are stored exactly
     already and are not wrapped. */
  var PROP_MASTER     = '_artwork_master';

  /* ---- pure: filenames ---------------------------------------------------- */

  function pad2(n) { return n < 10 ? '0' + n : '' + n; }

  /** UTC date, so a filename never depends on the shopper's timezone. */
  function isoDate(ms) {
    var d = new Date(typeof ms === 'number' && isFinite(ms) ? ms : Date.now());
    return d.getUTCFullYear() + '-' + pad2(d.getUTCMonth() + 1) + '-' + pad2(d.getUTCDate());
  }

  function extensionOf(name) {
    var m = /\.([A-Za-z0-9]{1,8})$/.exec(String(name || ''));
    return m ? m[1].toLowerCase() : '';
  }

  /* Strip everything Shopify might rewrite, so the name we choose is the name that
     comes back. The id is already restricted to A-Z0-9 and dashes. */
  function safeToken(s, fallback) {
    var out = String(s == null ? '' : s).replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
    return out || fallback;
  }

  /**
   * The three filenames, each carrying the designId and creation date.
   *
   *   SL-1KYJE-890PK-X7NKS__2026-07-27__original.png
   *   SL-1KYJE-890PK-X7NKS__2026-07-27__preview.png
   *   SL-1KYJE-890PK-X7NKS__2026-07-27__config.json
   *
   * Double underscore separates the fields because single underscores and dashes
   * both occur inside the id itself. Since ids are time-ordered, sorting Shopify's
   * file list by name also sorts it chronologically.
   */
  function assetFileNames(designId, createdAtMs, originalFileName, printFileName) {
    var base = safeToken(designId, 'SL-UNKNOWN') + '__' + isoDate(createdAtMs);
    var ext = extensionOf(originalFileName);
    var names = {
      original:   base + '__original' + (ext ? '.' + ext : ''),
      preview:    base + '__preview.png',
      config:     base + '__config.json',
      /* Always .svg — the production format is the exporter's, not the
         customer's, so this one name never inherits the upload's extension. */
      production: base + '__production.svg'
    };
    if (printFileName) names.print = base + '__print.' + (extensionOf(printFileName) || 'png');
    if (needsMaster(originalFileName)) names.master = names.original + '.zip';
    return names;
  }

  var MIME = {
    png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
    svg: 'image/svg+xml', pdf: 'application/pdf',
    webp: 'image/webp', bmp: 'image/bmp', avif: 'image/avif', heic: 'image/heic', heif: 'image/heif',
    tif: 'image/tiff', tiff: 'image/tiff', psd: 'image/vnd.adobe.photoshop', psb: 'image/vnd.adobe.photoshop',
    ai: 'application/postscript', eps: 'application/postscript'
  };
  function mimeOf(fileName) { return MIME[extensionOf(fileName)] || 'application/octet-stream'; }

  /** Every original except a PDF: those Shopify already stores byte for byte. */
  function needsMaster(fileName) { var e = extensionOf(fileName); return !!e && e !== 'pdf'; }

  /* ---- pure: a one-file, STORED (uncompressed) ZIP ------------------------
     The smallest valid archive: one local header, the bytes as they are, one
     central-directory entry, the end record. No compression, so nothing about
     the customer's bytes is re-encoded; the CRC-32 lets HQ prove they arrived
     intact. Returns a Uint8Array. */
  var CRC_TABLE = null;
  function crc32(bytes) {
    if (!CRC_TABLE) { CRC_TABLE = []; for (var n = 0; n < 256; n++) { var c = n; for (var k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; CRC_TABLE[n] = c >>> 0; } }
    var x = 0xffffffff;
    for (var i = 0; i < bytes.length; i++) x = CRC_TABLE[(x ^ bytes[i]) & 255] ^ (x >>> 8);
    return (x ^ 0xffffffff) >>> 0;
  }
  function zipStored(bytes, name) {
    if (!bytes || !bytes.length) return null;
    var data = (bytes instanceof Uint8Array) ? bytes : new Uint8Array(bytes);
    var nm = [], s = String(name || 'original');
    for (var i = 0; i < s.length; i++) { var ch = s.charCodeAt(i); nm.push(ch < 128 ? ch : 95); }   // ASCII name; '_' for the rest
    var crc = crc32(data), n = nm.length, len = data.length;
    var out = new Uint8Array(30 + n + len + 46 + n + 22), o = 0;
    function u16(v) { out[o++] = v & 255; out[o++] = (v >>> 8) & 255; }
    function u32(v) { u16(v & 0xffff); u16((v >>> 16) & 0xffff); }
    u32(0x04034b50); u16(20); u16(0); u16(0); u16(0); u16(0x21); u32(crc); u32(len); u32(len); u16(n); u16(0);
    for (var a = 0; a < n; a++) out[o++] = nm[a];
    out.set(data, o); o += len;
    var cd = o;
    u32(0x02014b50); u16(20); u16(20); u16(0); u16(0); u16(0); u16(0x21); u32(crc); u32(len); u32(len); u16(n); u16(0); u16(0); u16(0); u16(0); u32(0); u32(0);
    for (var b = 0; b < n; b++) out[o++] = nm[b];
    var cdLen = o - cd;
    u32(0x06054b50); u16(0); u16(0); u16(1); u16(1); u32(cdLen); u32(cd); u16(0);
    return out;
  }

  /** Pretty-printed on purpose — a human may open this from the order. */
  function configJson(payload) { return JSON.stringify(payload, null, 2); }

  /* ---- browser: blobs + FormData ------------------------------------------ */

  function hasBlob() { return typeof Blob !== 'undefined'; }

  /** data:image/png;base64,… -> Blob. Returns null for anything else. */
  function dataUrlToBlob(url) {
    if (!hasBlob() || typeof url !== 'string') return null;
    var m = /^data:([^;,]+)(;base64)?,(.*)$/.exec(url);
    if (!m) return null;
    var mime = m[1], isB64 = !!m[2], data = m[3];
    try {
      if (!isB64) return new Blob([decodeURIComponent(data)], { type: mime });
      var bin = atob(data), len = bin.length, bytes = new Uint8Array(len);
      for (var i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
      return new Blob([bytes], { type: mime });
    } catch (e) { return null; }
  }

  function bytesToBlob(bytes, mime) {
    if (!hasBlob() || !bytes) return null;
    try { return new Blob([bytes], { type: mime || 'application/octet-stream' }); }
    catch (e) { return null; }
  }

  /**
   * Build the multipart body for POST /cart/add.js.
   *
   * @param {Object} o
   *   lineItem         {id, quantity, properties} from SLCartAdapter
   *   payload          canonical payload (already carrying designId)
   *   designId         the minted id
   *   createdAtMs      mint time, for the filenames
   *   originalBytes    Uint8Array of the customer's file, unmodified
   *   originalName     its original filename
   *   previewDataUrl   the flattened sticker preview, as a data URL
   *
   * Files that cannot be produced are simply omitted — a missing preview must never
   * stop a customer ordering. What was and was not attached is reported back so the
   * caller can log it.
   */
  function buildAddToCartForm(o) {
    o = o || {};
    var li = o.lineItem || {};
    var props = li.properties || {};
    var names = assetFileNames(o.designId, o.createdAtMs, o.originalName, o.printBytes ? o.printName : null);
    var fd = new FormData();
    var attached = [], skipped = [];

    fd.append('id', li.id);
    fd.append('quantity', li.quantity);

    for (var k in props) {
      if (!Object.prototype.hasOwnProperty.call(props, k)) continue;
      var v = props[k];
      if (v === null || v === undefined || v === '') continue;
      fd.append('properties[' + k + ']', String(v));
    }

    function attach(prop, blob, name) {
      if (!blob) { skipped.push(prop); return; }
      try { fd.append('properties[' + prop + ']', blob, name); attached.push(prop); }
      catch (e) { skipped.push(prop); }
    }

    attach(PROP_ORIGINAL, bytesToBlob(o.originalBytes, mimeOf(o.originalName)), names.original);
    /* `omitMaster`: a caller whose production file already carries the
       original byte for byte (Cart Graphics) need not send it a third time.
       Unset, which is every other caller, nothing changes. */
    if (names.master && !o.omitMaster) {
      var z = null;
      try { z = zipStored(o.originalBytes, names.original); } catch (eZ) { z = null; }
      attach(PROP_MASTER, z ? bytesToBlob(z, 'application/zip') : null, names.master);
    }
    if (o.printBytes && names.print) attach(PROP_PRINT, bytesToBlob(o.printBytes, o.printMime || mimeOf(names.print)), names.print);
    attach(PROP_PREVIEW, dataUrlToBlob(o.previewDataUrl), names.preview);
    attach(PROP_CONFIG,
      hasBlob() && o.payload ? new Blob([configJson(o.payload)], { type: 'application/json' }) : null,
      names.config);
    /* The production SVG arrives as a STRING, not a data URL: it is built as
       text by sl-production-artwork.js, so there is nothing to decode and no
       round-trip through base64 to lose. A falsy value means the exporter
       refused to build one — which it does rather than emit a file missing the
       customer's design — and `attach` records that in `skipped`. */
    attach(PROP_PRODUCTION,
      (hasBlob() && typeof o.productionSvg === 'string' && o.productionSvg)
        ? new Blob([o.productionSvg], { type: 'image/svg+xml' }) : null,
      names.production);
    /* Only when the caller built one. A die cut has no sheet, so its absence there is
       not something that was skipped. */
    if (hasBlob() && typeof o.sheetLayoutSvg === 'string' && o.sheetLayoutSvg)
      attach(PROP_SHEET_LAYOUT, new Blob([o.sheetLayoutSvg], { type: 'image/svg+xml' }),
             names.production.replace(/__production.svg$/, '__sheet.svg'));

    return { formData: fd, fileNames: names, attached: attached, skipped: skipped };
  }

  var API = {
    version: 'design-assets-v1',
    PROP_ORIGINAL: PROP_ORIGINAL, PROP_PREVIEW: PROP_PREVIEW, PROP_CONFIG: PROP_CONFIG,
    PROP_PRODUCTION: PROP_PRODUCTION, PROP_PRINT: PROP_PRINT, PROP_MASTER: PROP_MASTER, PROP_SHEET_LAYOUT: PROP_SHEET_LAYOUT,
    // pure
    isoDate: isoDate, extensionOf: extensionOf, safeToken: safeToken,
    needsMaster: needsMaster, crc32: crc32, zipStored: zipStored,
    assetFileNames: assetFileNames, mimeOf: mimeOf, configJson: configJson,
    // browser
    dataUrlToBlob: dataUrlToBlob, bytesToBlob: bytesToBlob,
    buildAddToCartForm: buildAddToCartForm
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = API;
  if (root) root.SLDesignAssets = API;
})(typeof self !== 'undefined' ? self : this);
